import { useState } from 'react';
import { Accordion } from 'components/Accordion';
import { Chip } from 'common/components';
import { useGetWorkflowConfig } from 'services/hooks/queries';
import { PartyStatusDefinition, StatusTransition, TransitionRule } from 'services/api/WorkflowServiceAPI';
import styles from './StatusConfig.module.scss';

const VARIABLE_LABELS: Record<TransitionRule['variable'], string> = {
    disposition: 'Disposition',
    outbound_status: 'Call status',
};

const CONDITION_LABELS: Record<TransitionRule['condition'], string> = {
    last_contains: 'last is',
    last_not_contains: 'last is not',
    history_contains: 'history has',
    history_not_contains: 'history has no',
};

const describeRule = (rule: TransitionRule) =>
    `${VARIABLE_LABELS[rule.variable] ?? rule.variable} ${CONDITION_LABELS[rule.condition] ?? rule.condition}`;

const PartyStatuses = ({ statuses }: { statuses: PartyStatusDefinition[] }) => (
    <div className={styles.section}>
        <div className={styles.sectionHeader}>
            <span className={styles.sectionTitle}>Party statuses</span>
            <span className={styles.count}>{statuses.length}</span>
        </div>
        <div className={styles.statusGrid}>
            {statuses.map((status) => (
                <div key={status.name} className={styles.statusCard}>
                    <div className={styles.statusName}>{status.name}</div>
                    <div className={styles.statusMeta}>
                        <Chip
                            size='xs'
                            corner='pill'
                            variant='outlined'
                            color={status.terminal ? 'warning' : 'success'}
                        >
                            {status.terminal ? 'Terminal' : 'Active'}
                        </Chip>
                        {status.pushToCrm && (
                            <Chip size='xs' corner='pill' variant='outlined' color='info'>
                                {`CRM: ${status.crmIntegrationName || '—'}`}
                            </Chip>
                        )}
                    </div>
                </div>
            ))}
        </div>
        <p className={styles.hint}>
            Terminal statuses stop the workflow scheduling further outbound actions for that party.
        </p>
    </div>
);

const StatusTransitions = ({
    transitions,
    knownStatuses,
}: {
    transitions: StatusTransition[];
    knownStatuses: Set<string>;
}) => (
    <div className={styles.section}>
        <div className={styles.sectionHeader}>
            <span className={styles.sectionTitle}>Status transitions</span>
            <span className={styles.count}>{transitions.length}</span>
        </div>
        <p className={styles.hint}>Evaluated top to bottom. The first transition whose rules all hold wins.</p>
        <ol className={styles.transitionList}>
            {transitions.map((transition, index) => {
                const joiner = transition.logic === 'or' ? 'OR' : 'AND';
                const targetMissing = !knownStatuses.has(transition.target);
                return (
                    <li key={`${transition.target}-${index}`} className={styles.transition}>
                        <span className={styles.order}>{index + 1}</span>
                        <div className={styles.transitionBody}>
                            <div className={styles.rules}>
                                {transition.rules.map((rule, ruleIndex) => (
                                    <span key={`${rule.variable}-${rule.value}-${ruleIndex}`} className={styles.rule}>
                                        {ruleIndex > 0 && <span className={styles.joiner}>{joiner}</span>}
                                        <span className={styles.ruleText}>
                                            {describeRule(rule)} <code className={styles.ruleValue}>{rule.value}</code>
                                        </span>
                                    </span>
                                ))}
                            </div>
                            <div className={styles.target}>
                                <span className={styles.arrow}>→</span>
                                <Chip
                                    size='xs'
                                    corner='pill'
                                    variant='outlined'
                                    color={targetMissing ? 'warning' : 'primary'}
                                >
                                    {transition.target}
                                </Chip>
                                {targetMissing && (
                                    <span className={styles.warning}>target is not a configured party status</span>
                                )}
                            </div>
                        </div>
                    </li>
                );
            })}
        </ol>
    </div>
);

/**
 * Read-only view of the workflow's configurable status model: the party status
 * vocabulary and the transition rules post-processing evaluates against it.
 */
export const StatusConfig = ({ workflowId }: { workflowId: string }) => {
    const [isOpen, setIsOpen] = useState(false);
    const { workflowConfig, isLoadingWorkflowConfig } = useGetWorkflowConfig(workflowId);

    const partyStatuses = workflowConfig?.config?.partyStatuses ?? [];
    const statusTransitions = workflowConfig?.config?.statusTransitions ?? [];
    const hasConfig = partyStatuses.length > 0 || statusTransitions.length > 0;
    const knownStatuses = new Set(partyStatuses.map((status) => status.name));

    return (
        <Accordion
            isOpen={isOpen}
            onToggle={() => setIsOpen(!isOpen)}
            className={styles.accordion}
            header={
                <div className={styles.header}>
                    <span>Status configuration</span>
                    {!isLoadingWorkflowConfig && hasConfig && (
                        <span className={styles.headerMeta}>
                            {partyStatuses.length} statuses · {statusTransitions.length} transitions
                        </span>
                    )}
                </div>
            }
        >
            {isLoadingWorkflowConfig ? (
                <p className={styles.empty}>Loading status configuration…</p>
            ) : !hasConfig ? (
                <p className={styles.empty}>
                    This workflow has no configured party statuses or transitions. Post-processing will record a
                    disposition but leave the party status unchanged.
                </p>
            ) : (
                <div className={styles.body}>
                    {partyStatuses.length > 0 && <PartyStatuses statuses={partyStatuses} />}
                    {statusTransitions.length > 0 && (
                        <StatusTransitions transitions={statusTransitions} knownStatuses={knownStatuses} />
                    )}
                    {statusTransitions.length === 0 && (
                        <p className={styles.empty}>
                            No transitions configured, so no call outcome will change the party status.
                        </p>
                    )}
                </div>
            )}
        </Accordion>
    );
};
